import React, { useState, useRef } from 'react';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonFunctions,
  buttonTextSizes,
} from '../../shared/Button';
import InlineHelpText from '../../shared/InlineHelpText';
import {
  submitText,
  cancelText,
  titleText,
  searchText,
  typeText,
  descriptionText,
  idText,
} from '../../shared/SharedInternationalizations';
import HelpIcon from '../../shared/HelpIcon';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import { connect } from 'react-redux';
import SvgExclamationPointIcon from '../../svg/ExclamationPointIcon';
import SvgCheckMarkIcon from '../../svg/CheckMarkIcon';
import Overlay from '../../shared/Overlay';
import { Condition } from '../../shared/Condition';

export function UnconnectedUnitCheckinPreferencesForm(props) {
  const {
    unit,
    onSubmit,
    checkinModes,
    alertDeviceTypes,
    previous = '/units',
    checkinCall = 15,
    usersObj,
  } = props;
  const devicesArr = unit.available_checkin_devices || unit.alert_devices || [];
  const checkinModesArr = Object.values(checkinModes);
  const activeCheckinModesArr = checkinModesArr.filter(
    (mode) => mode.active === 1 && mode.checkin === 1
  );

  let userCheckin = false;
  if (usersObj && unit) {
    userCheckin = Object.values(usersObj).some(
      (u) =>
        u &&
        u.active_unit === unit.id &&
        u.checkin_profile &&
        u.checkin_profile.checkin_modes &&
        u.checkin_profile.checkin_modes.length > 0
    );
  }

  const [checkinModesFilter, setCheckinModesFilter] = useState('');

  const [devicesSearchFilter, setDevicesSearchFilter] = useState('');
  const [
    displayCheckInReminderHelpText,
    setDisplayCheckInReminderHelpText,
  ] = useState(false);
  function toggleDisplayCheckInReminderHelpText(value) {
    setDisplayCheckInReminderHelpText(value);
  }
  const formFilter = (event) => {
    const value = event.target.value;
    setCheckinModesFilter(value);
  };

  const checkInReminderHelpTextRef = useRef();
  const checkInReminderHelpText = (
    <FormattedMessage
      id="unitEdit.checkInReminderHelpText"
      defaultMessage="If the unit has not yet checked in by <b>15</b> minutes before the end of the check-in period, they should be called to check-in."
      description="Help text for understanding what the unit check-in exclude means."
      values={{ b: () => checkinCall }}
    />
  );
  const [displayCheckInCallHelpText, setDisplayCheckInCallHelpText] = useState(
    false
  );
  function toggleDisplayCheckInCallHelpText(value) {
    setDisplayCheckInCallHelpText(value);
  }
  const checkInCallHelpTextRef = useRef();
  const checkInCallHelpText = (
    <FormattedMessage
      id="unitEdit.checkInCallHelpText"
      defaultMessage="This unit should be called to check it in."
      description="Help text for understanding what the unit check-in exclude means."
    />
  );
  const [
    displayCheckInChildrenHelpText,
    setDisplayCheckInChildrenHelpText,
  ] = useState(false);
  function toggleShowCheckInChildrenHelpText(value) {
    setDisplayCheckInChildrenHelpText(value);
  }
  const checkInChildrenHelpTextRef = useRef();
  const checkInChildrenHelpText = (
    <FormattedMessage
      id="unitEdit.checkInChildrenHelpText"
      defaultMessage="Checking in this unit will also check in any child units associated with this unit."
      description="Help text for understanding what the unit check-in exclude means."
    />
  );

  const [showSelectedModesHelpText, setShowSelectedModesHelpText] = useState(
    false
  );
  function toggleSelectedModesHelpText(value) {
    setShowSelectedModesHelpText(value);
  }
  const selectedModesHelpTextRef = useRef();
  const selectedModesHelpText = (
    <FormattedMessage
      id="unitCheckinEdit.selectedModesHelpText"
      defaultMessage="These are checkin modes that are selected"
    />
  );
  const checkinModesText = (
    <FormattedMessage
      id="unitCheckinEdit.checkinModesText"
      defaultMessage="Select Check-in Modes"
    />
  );

  const [showSelectedDeviceHelpText, setShowSelectedDeviceHelpText] = useState(
    false
  );
  function toggleSelectedDeviceHelpText(value) {
    setShowSelectedDeviceHelpText(value);
  }
  const selectedDeviceHelpTextRef = useRef();

  const devicesText = (
    <FormattedMessage
      id="unitCheckinEdit.devicesText"
      defaultMessage="Select Check-in Device"
    />
  );
  const selectedDeviceHelpText = (
    <FormattedMessage
      id="unitCheckinEdit.selectedDeviceHelpText"
      defaultMessage="The selected device will be used to contact the unit when they need to be contacted for checking in."
    />
  );

  const devicesFilterSet = (event) => {
    const value = event.target.value;
    setDevicesSearchFilter(value);
  };

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const checkinModesTable = (values) => {
    const initialFilterList = activeCheckinModesArr.filter((mode) => {
      if (values.checkin_modes.includes(mode.mode_num)) {
        return false;
      } else {
        return true;
      }
    });

    const modesArray = initialFilterList.map((mode) => {
      return {
        selector: {
          sortBy: mode.mode_num,
          display: mode.mode_num,
        },
        title: {
          sortBy: mode.mode_name.toUpperCase(),
          display: mode.mode_name,
        },
      };
    });

    let searchedModes = [];
    // Take the array and filter based on the text entered in the search bar.
    if (checkinModesFilter !== '') {
      searchedModes = modesArray.filter((item) => {
        let filterResult = false;
        // Make the device name uppercase for case insensitivity
        const title = item.title.display.toUpperCase();
        const id = item.title.display;
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        if (
          title.includes(checkinModesFilter.toUpperCase()) ||
          checkinModesFilter.includes(id)
        ) {
          filterResult = true;
        }
        return filterResult;
      });
    } else {
      searchedModes = modesArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'title',
        display: titleText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="checkin_modes"
        selectorParamValue="checkin_modes"
        sortParam="title"
        tableData={searchedModes}
        selectorType={selectorTypes.checkbox}
        tableStyles="table__constrained table-layout__1-column--selector"
        data-test="modes-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedCheckinModesTable = (values) => {
    let table;
    let selectedModes = values.checkin_modes;

    let modesRows;
    if (selectedModes.length > 0) {
      if (selectedModes) {
        const filteredModes = [];
        selectedModes.map((id) => {
          let thisId = id;
          if (typeof id === 'string') {
            thisId = parseInt(thisId);
          }
          if (checkinModes[thisId]) {
            filteredModes.push(checkinModes[thisId]);
          }
          return '';
        });
        modesRows = filteredModes.map((d) => {
          const removeButton = (
            <label>
              <Field
                className="visually-hidden"
                name="checkin_modes"
                component="input"
                type="checkbox"
                value={d.mode_num}
              />
              <SvgXIcon />
            </label>
          );

          return {
            selector: {
              sortBy: '',
              display: removeButton,
            },

            title: {
              sortBy: d.mode_name.toUpperCase(),
              display: d.mode_name,
            },
          };
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: true,
          sortBy: 'title',
          display: titleText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="title"
          tableData={modesRows}
          tableStyles="table__constrained table-layout__1-column--selector"
          data-test="selected-modes-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  /**
   * Function to generate the users table
   * @returns {object} - The table of users
   */
  const devicesTable = (values) => {
    const deviceCheck = devicesArr.filter((d) => {
      if (
        d.alert_device_type !== 3 &&
        d.alert_device_type !== 33 &&
        d.alert_device_type !== 24
      ) {
        return false;
      } else {
        return true;
      }
    });
    // map over the users and put into the format that is required for the table component
    if (deviceCheck.length > 0) {
      const initialFilterList = devicesArr.filter((d) => {
        let deviceId = values.checkin_device;
        if (typeof deviceId === 'string') {
          deviceId = parseInt(deviceId);
        }
        if (deviceId === d.alert_device_id) {
          return false;
        } else if (
          d.alert_device_type !== 3 &&
          d.alert_device_type !== 33 &&
          d.alert_device_type !== 24
        ) {
          return false;
        } else {
          return true;
        }
      });

      const devicesArray = initialFilterList.map((d) => {
        let deviceType = '';
        if (alertDeviceTypes[d.alert_device_type]) {
          deviceType = alertDeviceTypes[d.alert_device_type].description;
        }
        return {
          selector: {
            sortBy: d.alert_device_id,
            display: d.alert_device_id,
          },
          id: {
            sortBy: d.alert_device_id,
            display: d.alert_device_id,
          },
          description: {
            sortBy: d.alert_description.toUpperCase(),
            display: d.alert_description,
          },
          type: {
            sortBy: deviceType.toUpperCase(),
            display: deviceType,
          },
        };
      });

      let searchedDevices = [];
      // Take the array and filter based on the text entered in the search bar.

      if (devicesSearchFilter !== '') {
        searchedDevices = devicesArray.filter((item) => {
          let filterResult = false;
          // Make the device name uppercase for case insensitivity
          const description = item.description.display.toUpperCase();
          const type = item.type.display.toUpperCase();
          const id = item.id.display;
          // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
          if (
            description.includes(devicesSearchFilter.toUpperCase()) ||
            devicesSearchFilter.includes(id) ||
            type.includes(devicesSearchFilter.toUpperCase())
          ) {
            filterResult = true;
          }
          return filterResult;
        });
      } else {
        searchedDevices = devicesArray;
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: true,
          sortBy: 'id',
          display: idText,
        },
        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: true,
          sortBy: 'type',
          display: typeText,
        },
      ];
      // generates the actual table
      const table = (
        <Table
          hasTableHead={true}
          hasSelector={true}
          tableHeadings={tableHeadings}
          selectorName="checkin_device"
          selectorParamValue="checkin_device"
          sortParam="id"
          tableData={searchedDevices}
          selectorType={selectorTypes.radio}
          tableStyles="table__constrained table-layout__3-column--selector"
          data-test="checkin-devices-table"
        />
      );

      return (
        <React.Fragment>
          <div className="content-display__column">
            <div className="form__item-container">
              <div className="align-center">
                <label
                  className="text-medium text-bold form-item__field-message--required"
                  htmlFor="usersId">
                  {devicesText}
                </label>
                <HelpIcon
                  onClick={() =>
                    selectedDeviceHelpTextRef.current.toggleHelpText()
                  }
                  toggled={showSelectedDeviceHelpText}
                  styles="margin-left-5"
                />
              </div>
            </div>
          </div>
          <div className="width-100 margin-bottom-10">
            <InlineHelpText
              ref={selectedDeviceHelpTextRef}
              toggleFunction={toggleSelectedDeviceHelpText}
              message={selectedDeviceHelpText}
            />
          </div>
          <div className="content-display__column">
            {selectedDevicesTable(values)}

            <div className="table__header padding-10">
              <label className="text-medium text-bold" htmlFor="usersFilter">
                {searchText}:
              </label>
              <input
                type="text"
                id="devicesFilter"
                name="devicesFilter"
                onChange={devicesFilterSet}
                className="message-table__filter-search-box margin-left-10"
                value={devicesSearchFilter}
              />
            </div>

            {table}
          </div>
        </React.Fragment>
      );
    } else {
      return '';
    }
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedDevicesTable = (values) => {
    let table;
    let deviceId = values.checkin_device;

    if (deviceId) {
      if (typeof deviceId === 'string') {
        deviceId = parseInt(deviceId);
      }
      const filteredDevice = devicesArr.filter(
        (dev) => dev.alert_device_id === deviceId
      );

      const deviceRows = filteredDevice.map((d) => {
        const removeButton = (
          <label>
            <Field
              className="visually-hidden"
              name="checkin_device"
              component="input"
              type="radio"
              value={''}
            />
            <SvgXIcon />
          </label>
        );
        let deviceType = '';
        if (alertDeviceTypes[d.alert_device_type]) {
          deviceType = alertDeviceTypes[d.alert_device_type].description;
        }

        return {
          button: {
            sortBy: '',
            display: removeButton,
          },
          id: {
            sortBy: d.alert_device_id,
            display: d.alert_device_id,
          },
          description: {
            sortBy: d.alert_description.toUpperCase(),
            display: d.alert_description,
          },
          type: {
            sortBy: deviceType.toUpperCase(),
            display: deviceType,
          },
        };
      });

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: false,
          sortBy: 'id',
          display: idText,
        },
        {
          isSortable: false,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: false,
          sortBy: 'type',
          display: typeText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="id"
          tableData={deviceRows}
          tableStyles="table__constrained table-layout__3-column--selector"
          data-test="selected-checkin-devices-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  const [showCheck, setShowCheck] = useState(false);
  function noModeConfirmation() {
    setShowCheck(true);
  }

  function cancelNoMode() {
    setShowCheck(false);
  }

  /**
   * Function to display the archive challenge notification modal
   */
  function displayNoMode() {
    if (showCheck) {
      return (
        <Overlay
          rootUrl={`/units/view/${unitInfo.id}/edit/?mode=checkInPreferences`}
          canClose={false}
          data-test="unit-archive-challenge-overlay"
          useFunction={true}
          onClick={cancelNoMode}>
          <div
            className="content-container content-container--column"
            data-test="archive-overlay">
            <h1 className="no-margin">
              <FormattedMessage
                id="unitCheckinPreferencesForm.noModeChallenge"
                defaultMessage="There are no check-in modes selected."
                description="The parent unit of the unit being created"
              />
            </h1>

            <hr />

            <React.Fragment>
              <span className="text-alert text-bold">
                <FormattedMessage
                  id="unitCheckinPreferencesForm.noModeChallengeText"
                  defaultMessage="To use check-in features, there must be a check-in mode selected. If the check-in profile is saved without a check-in mode, it will be inactive."
                  description="The parent unit of the unit being created"
                />
              </span>
              <hr />
            </React.Fragment>

            <div className="flex-end width-100">
              <Button
                data-test="cancel-archive"
                onClick={cancelNoMode}
                text={cancelText}
                buttonType={buttonTypes.noButton}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin margin-right-15 js-cancel-archive-button"
              />
              <Button
                data-test="submit-archive"
                buttonFunction={buttonFunctions.submit}
                text={submitText}
                buttonType={buttonTypes.danger}
                buttonSize={buttonSizes.small}
                buttonTextSize={buttonTextSizes.mediumSmall}
                additionalStyles="no-margin  js-submit-archive-button"
              />
            </div>
          </div>
        </Overlay>
      );
    } else {
      return '';
    }
  }

  const pref = unit.checkin_profile;

  let unitInfo = {
    checkin_required: true,
    unit: unit.id,
    checkin_modes: [],
  };

  if (pref && Object.keys(pref).length > 0) {
    unitInfo = {
      unit: pref.unit,
      receive_checkin_reminder: pref.receive_checkin_reminder,
      call_for_checkin: pref.call_for_checkin,
      call_for_checkin_time: pref.call_for_checkin_time,
      checks_in_children: pref.checks_in_children,
      checkin_modes: pref.checkin_modes,
      checkin_device: pref.checkin_device,
    };
  }

  return (
    <div className="content-display__container">
      <Form
        data-test="unit-checkin-preferences-form"
        onSubmit={onSubmit}
        keepDirtyOnReinitialize={true}
        initialValues={{
          ...unitInfo,
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form onSubmit={handleSubmit} className="width-100">
            {displayNoMode()}
            {values.checkin_modes.length === 0 ||
            activeCheckinModesArr.length === 0 ? (
              <div className="content-display__column">
                <div className="content-display__item-pill-container">
                  <div className="item-container__info-container">
                    <SvgExclamationPointIcon className="text-alert" />
                  </div>
                  <div className="item-container__main-container">
                    <div className="text-large text-light text-alert align-center">
                      <FormattedMessage
                        data-test="unit-no-checkin-devices-message"
                        id="unitCheckinPreferencesForm.noSelectedModes"
                        defaultMessage="To use check-in features, there must be a check-in mode selected. If the check-in profile is saved without a check-in mode, it will be inactive."
                        description="The check-in instructions"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              ''
            )}
            {devicesArr.length === 0 ? (
              <div className="content-display__column">
                <div className="content-display__item-pill-container">
                  <div className="item-container__info-container">
                    <SvgExclamationPointIcon className="text-alert" />
                  </div>
                  <div className="item-container__main-container">
                    <div className="text-large text-light text-alert align-center">
                      <FormattedMessage
                        data-test="unit-no-checkin-devices-message"
                        id="unitCheckinPreferencesForm.noCallForCheckinDevice"
                        defaultMessage="This unit is not able to be called for a check-in or called to be reminded to check-in. An associated phone device is required in order to use this functionality."
                        description="The check-in instructions"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              ''
            )}
            <Field
              className="visually-hidden"
              name="unit"
              component="input"
              type="text"
            />
            <div className="content-display__container">
              {values.checkin_modes.length > 0 ? (
                <React.Fragment>
                  <div className="content-display__column content-display__column">
                    <div className="content-display__item-container">
                      <div className="item-container__main-container">
                        <Field
                          name="receive_checkin_reminder"
                          data-test="receive_checkin_reminder"
                          id="receive_checkin_reminder"
                          component="input"
                          type="checkbox"
                          className="visually-hidden"
                          disabled={devicesArr.length === 0}
                        />
                        <label
                          className={
                            devicesArr.length === 0
                              ? 'text-large text-dimmed text-light form-item__field-toggle'
                              : 'text-large text-light text-deep-blue form-item__field-toggle'
                          }
                          htmlFor="receive_checkin_reminder">
                          <FormattedMessage
                            id="addEditunitCheckinPreferencesForm.receiveCheckinReminderText"
                            defaultMessage="Contact this unit <b>15</b> minutes before the end of the check-in period if the unit has not checked in yet"
                            description="Check-in options"
                            values={{ b: () => checkinCall }}
                          />
                        </label>
                      </div>
                      <div className="item-container__info-container">
                        <HelpIcon
                          styles="margin-left-5 text-large"
                          onClick={() =>
                            checkInReminderHelpTextRef.current.toggleHelpText()
                          }
                          toggled={displayCheckInReminderHelpText}
                        />
                      </div>
                    </div>

                    <InlineHelpText
                      ref={checkInReminderHelpTextRef}
                      isToggled={toggleDisplayCheckInReminderHelpText}
                      message={checkInReminderHelpText}
                    />
                  </div>
                  <div className="content-display__column content-display__column--2-3">
                    <div className="content-display__item-container">
                      <div className="item-container__main-container">
                        <Field
                          name="call_for_checkin"
                          data-test="call_for_checkin"
                          id="call_for_checkin"
                          component="input"
                          type="checkbox"
                          className="visually-hidden"
                          disabled={devicesArr.length === 0}
                        />
                        <label
                          className={
                            devicesArr.length === 0
                              ? 'text-large text-dimmed text-light form-item__field-toggle'
                              : 'text-large text-light text-deep-blue form-item__field-toggle'
                          }
                          htmlFor="call_for_checkin">
                          <FormattedMessage
                            id="unitCheckinPreferencesForm.callForCheckinText"
                            defaultMessage="Call this unit for a daily check-in"
                            description="Check-in options"
                          />
                        </label>
                      </div>
                      <div className="item-container__info-container">
                        <HelpIcon
                          styles="margin-left-5"
                          onClick={() =>
                            checkInCallHelpTextRef.current.toggleHelpText()
                          }
                          toggled={displayCheckInCallHelpText}
                        />
                      </div>
                    </div>

                    <InlineHelpText
                      ref={checkInCallHelpTextRef}
                      isToggled={toggleDisplayCheckInCallHelpText}
                      message={checkInCallHelpText}
                    />
                  </div>
                </React.Fragment>
              ) : (
                ''
              )}
              <div className="content-display__column">
                <Condition when="call_for_checkin" is={true}>
                  <div className="content-display__item-container">
                    <div className="item-container__main-container">
                      <label
                        className="text-large text-light text-deep-blue "
                        htmlFor="call_for_checkin">
                        <FormattedMessage
                          id="unitCheckinPreferencesForm.callCheckinReminderText"
                          defaultMessage="Time to call:"
                          description="Check-in options"
                        />
                      </label>
                      <Field name="call_for_checkin_time">
                        {(props) => (
                          <TimePicker
                            name={props.input.name}
                            onChange={props.input.onChange}
                            disableClock={true}
                            value={props.input.value}
                          />
                        )}
                      </Field>
                    </div>
                    <div className="item-container__info-container"></div>
                  </div>
                </Condition>
              </div>
              <div className="content-display__column content-display__column">
                <div className="content-display__item-container">
                  <div className="item-container__main-container">
                    <div className="">
                      <Field
                        name="checks_in_children"
                        data-test="checks_in_children"
                        id="checks_in_children"
                        component="input"
                        type="checkbox"
                        className="visually-hidden"
                      />
                      <label
                        className="text-large text-light text-deep-blue form-item__field-toggle"
                        htmlFor="checks_in_children">
                        <FormattedMessage
                          id="unitCheckinPreferencesForm.checkInExcludeText"
                          defaultMessage="Check in any child units"
                          description="Check-in options"
                        />
                      </label>
                    </div>
                  </div>
                  <div className="item-container__info-container">
                    <HelpIcon
                      styles="margin-left-5"
                      onClick={() =>
                        checkInChildrenHelpTextRef.current.toggleHelpText()
                      }
                      toggled={displayCheckInChildrenHelpText}
                    />
                  </div>
                </div>
                <InlineHelpText
                  ref={checkInChildrenHelpTextRef}
                  isToggled={toggleShowCheckInChildrenHelpText}
                  message={checkInChildrenHelpText}
                />
              </div>
              {userCheckin ? (
                <div className="content-display__column">
                  <div className="content-display__item-pill-container">
                    <div className="item-container__info-container">
                      <SvgCheckMarkIcon className="text-success" />
                    </div>
                    <div className="item-container__main-container">
                      <div className="text-large text-light text-success align-center">
                        <FormattedMessage
                          id="unitCheckinPreferencesForm.userCheckinModes"
                          defaultMessage="Users assigned to this unit have their own check-in profile. Please check the User's profile for the Check-in Mode."
                          description="Message shown when assigned users have their own check-in profiles"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : activeCheckinModesArr.length > 0 ? (
                <React.Fragment>
                  <div className="content-display__column">
                    <div className="form__item-container">
                      <div className="align-center">
                        <label
                          className="text-medium text-bold"
                          htmlFor="usersId">
                          {checkinModesText}
                        </label>
                        <HelpIcon
                          onClick={() =>
                            selectedModesHelpTextRef.current.toggleHelpText()
                          }
                          toggled={showSelectedModesHelpText}
                          styles="margin-left-5"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="width-100 margin-bottom-10">
                    <InlineHelpText
                      ref={selectedModesHelpTextRef}
                      isToggled={toggleSelectedModesHelpText}
                      message={selectedModesHelpText}
                    />
                  </div>
                  <div className="content-display__column">
                    {selectedCheckinModesTable(values)}

                    <div className="table__header padding-10">
                      <label
                        className="text-medium text-bold"
                        htmlFor="modesFilter">
                        {searchText}:
                      </label>

                      <input
                        type="text"
                        id="modesFilter"
                        name="modesFilter"
                        onChange={formFilter}
                        className="message-table__filter-search-box margin-left-10"
                        value={checkinModesFilter}
                      />
                    </div>

                    {checkinModesTable(values)}
                  </div>
                </React.Fragment>
              ) : (
                <div className="content-display__column">
                  <div className="content-display__item-pill-container">
                    <div className="item-container__info-container">
                      <SvgExclamationPointIcon className="text-alert" />
                    </div>
                    <div className="item-container__main-container">
                      <div className="text-large text-light text-alert align-center">
                        <FormattedMessage
                          data-test="unit-no-checkin-devices-message"
                          id="unitCheckinPreferencesForm.noModes"
                          defaultMessage="No check-in modes are available to be assigned."
                          description="The check-in instructions"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {values.receive_checkin_reminder || values.call_for_checkin
                ? devicesTable(values)
                : ''}

              <div className="width-100 margin-bottom-10">
                <hr />
                <div className="form__section-container form__section-container--spread">
                  <div className="form__items-group-container">
                    <Link
                      className="link link-text"
                      to={previous}
                      data-test="unit-checkin-preferences-form-cancel-link">
                      {cancelText}
                    </Link>
                  </div>
                  <div className="form__items-group-container">
                    {values.checkin_modes.length === 0 ? (
                      <Button
                        onClick={noModeConfirmation}
                        text={submitText}
                        buttonType={buttonTypes.success}
                        buttonSize={buttonSizes.small}
                        additionalStyles={'no-margin margin-left-15'}
                        disabled={
                          // No I don't like this either, but every other way I found to add this logic in wasn't being checked when the form is used so this was the only way I found to have the logic in place and checking every time anything is touched on the form.
                          invalid ||
                          (values.call_for_checkin
                            ? values.call_for_checkin_time
                              ? false
                              : true
                            : false) ||
                          (values.call_for_checkin ||
                          values.receive_checkin_reminder
                            ? values.checkin_modes.length > 0 &&
                              values.checkin_device
                              ? false
                              : true
                            : false)
                        }
                      />
                    ) : (
                      <Button
                        buttonFunction={buttonFunctions.submit}
                        text={submitText}
                        buttonType={buttonTypes.success}
                        buttonSize={buttonSizes.small}
                        additionalStyles={'no-margin margin-left-15'}
                        disabled={
                          // No I don't like this either, but every other way I found to add this logic in wasn't being checked when the form is used so this was the only way I found to have the logic in place and checking every time anything is touched on the form.
                          invalid ||
                          (values.call_for_checkin
                            ? values.call_for_checkin_time
                              ? false
                              : true
                            : false) ||
                          (values.call_for_checkin ||
                          values.receive_checkin_reminder
                            ? values.checkin_modes.length > 0 &&
                              values.checkin_device
                              ? false
                              : true
                            : false)
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
}
const mapStateToProps = (state) => {
  return {
    alertDeviceTypes: state.devices.alertDeviceTypes,
    checkinCall: state.system.siteInfo.checkin_reminder_time,
    usersObj: state.users.users,
  };
};

export default connect(mapStateToProps)(UnconnectedUnitCheckinPreferencesForm);
